import {
    DropDown,
    getFilterElementsDefault,
    toInt,
    useErrorState,
    useWorkplaceState,
    WorkplaceBtnActions,
    WorkplaceBtnDelete
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addServiceDiscount,
    deleteServiceDiscount,
    getServiceDiscountExcel,
    getServiceDiscountItem,
    getServiceDiscountList,
    updateServiceDiscount
} from 'axios/serviceDiscountApi';
import { getTariffPlansList } from 'axios/tariffsApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import Money from 'components/Common/Money';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    ServiceDiscountCreate,
    ServiceDiscountFilter,
    ServiceDiscountTable,
    ServiceDiscountUpdate
} from 'models/api/serviceDiscount';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import ServiceDiscountAddButton from './ServiceDiscountAddButton';
import ServiceDiscountChangeModal from './ServiceDiscountChangeModal';
import Filter from './ServiceDiscountFilter';

const ServiceDiscount = () => {
    const api = useApi();
    const { hasRole } = useAuth();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isModalVisible, setModalVisible] = useState(false);
    const { handleRequestError } = useErrorState();

    const [tariffPlans, setTariffPlans] = useState<DropDown[]>([]);

    const { isFetching: tariffPlansLoading } = useQuery(
        'tariffPlansList',
        () => getTariffPlansList(api),
        {
            onSuccess: (data) => data && setTariffPlans(data),
            onError: handleRequestError,
            enabled: tariffPlans.length === 0,
        }
    );

    return (
        <BOWorkplace<
            ServiceDiscountTable,
            ServiceDiscountTable,
            ServiceDiscountFilter,
            ServiceDiscountUpdate,
            ServiceDiscountCreate
        >
            pageTitle="BackOfficeServiceDiscount"
            initColumns={[
                {
                    key: 'tariffPlanName',
                    name: <Trans i18nKey="BackOfficeTariffPlan" />,
                },
                {
                    key: 'startAmount',
                    name: <Trans i18nKey="BackOfficeStartAmount" />,
                    formatter: ({ row }) => <Money value={row.startAmount} />,
                },
                {
                    key: 'endAmount',
                    name: <Trans i18nKey="BackOfficeEndAmount" />,
                    formatter: ({ row }) => <Money value={row.endAmount} />,
                },
                {
                    key: 'approvalFeeDiscount',
                    name: <Trans i18nKey="BackOfficeApprovalFeeDiscount" />,
                    formatter: ({ row }) => <Money value={row.approvalFeeDiscount} nullable />,
                },
                {
                    key: 'loanServiceFeeDiscount',
                    name: <Trans i18nKey="BackOfficeLoanServiceFeeDiscount" />,
                    formatter: ({ row }) => <Money value={row.loanServiceFeeDiscount} nullable />,
                },
                {
                    key: 'guaranteeFeePercent',
                    name: <Trans i18nKey="BackOfficeGuaranteeFeePercent" />,
                    formatter: ({ row }) => <Money value={row.guaranteeFeePercent} nullable />,
                },
                {
                    key: 'extensionApprovalFeeDiscount',
                    name: <Trans i18nKey="BackOfficeExtensionApprovalFeeDiscount" />,
                    formatter: ({ row }) => (
                        <Money value={row.extensionApprovalFeeDiscount} nullable />
                    ),
                },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'base-data-grid__cell_centered no-change-modal',
                    headerCellClass: 'base-data-grid__cell_centered',
                    width: 50,
                    minWidth: 50,
                    resizable: false,
                    clickDisable: true,
                    visible: hasRole('serviceDiscountDelete'),
                    formatter: ({ row }) => (
                        <WorkplaceBtnActions>
                            <WorkplaceBtnDelete key={`delete-btn-${row.id}`} id={row.id} />
                        </WorkplaceBtnActions>
                    ),
                },
            ]}
            storagePath="serviceDiscounts/columns"
            fetchQueryFn={(filter) => getServiceDiscountList(api, filter)}
            queryKey={['serviceDiscount']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getServiceDiscountExcel(api, filter)}
            showExcelBtn={hasRole('serviceDiscountExcel')}
            filter={<Filter tariffPlans={tariffPlans} />}
            filterParams={{
                tariffPlanId: null,
            }}
            getFilterElements={getFilterElementsDefault([
                {
                    name: t('BackOfficeTariffPlan'),
                    id: 'tariffPlanId',
                    formatter: (val) => {
                        if (!tariffPlansLoading) {
                            const tariffPlan = tariffPlans.find((i) => i.id === toInt(val));
                            return tariffPlan?.name ?? '';
                        } else {
                            return val;
                        }
                    },
                },
            ])}
            buttons={
                hasRole('serviceDiscountCreate')
                    ? [<ServiceDiscountAddButton key="1" showModal={() => setModalVisible(true)} />]
                    : []
            }
            modal={{
                component: (
                    <ServiceDiscountChangeModal
                        isOpen={isModalVisible}
                        toggle={setModalVisible}
                        tariffPlans={tariffPlans}
                    />
                ),
                fetchCurrentItemQueryFn: (id) => getServiceDiscountItem(api, id),
                addMutationFn: (item) => addServiceDiscount(api, item),
                changeMutationFn: (item) => updateServiceDiscount(api, item),
            }}
            showModal={hasRole('serviceDiscountGetById') ? () => setModalVisible(true) : undefined}
            deleteMutationFn={(item) => deleteServiceDiscount(api, item)}
        />
    );
};

const useServiceDiscountWorkplaceState = () =>
    useWorkplaceState<
        ServiceDiscountTable,
        ServiceDiscountTable,
        ServiceDiscountUpdate,
        ServiceDiscountCreate
    >();

export default ServiceDiscount;
export { useServiceDiscountWorkplaceState };
